/**
 * @fileoverview `strings-kit` — the ONE interpolator and the ONE missing-translation resolver that
 *   every `@puffin-core` package shipping a string bundle builds its `strings/` leaves from.
 * @package @puffin-core/i18n-bootstrap
 *
 * I18N-RENDER-002 Phase 0 (2026-08-20).
 *
 * ## What it replaces
 *
 * Measured on 2026-08-20, before this module existed:
 *
 * | helper | copies | how they differed |
 * | ------ | -----: | ----------------- |
 * | `resolveString` | **7** | only the imported defaults constant and the docblock prose |
 * | `interpolate`   | **8** | two brace syntaxes, three missing-token policies, four edge-case behaviours |
 *
 * Seven identical implementations is not a coincidence, it is a rule-of-three violation that ran to
 * seven. And the eight interpolators were *not* identical, which is worse: three of them crashed on
 * a nullish template, one expanded `$&` in a value as a regex replacement pattern, and one read
 * prototype keys. Every one of those is a defect a caller could not see from the call site.
 *
 * ## 🔴 Why this is a SUBPATH and is deliberately NOT in the package barrel
 *
 * Its consumers are `packages/<pkg>/lib/esm/strings/*` leaves, and that subtree is **leaf-only by
 * design**: `@puffin-core/i18n-namespaces` imports each package's narrow `/strings` subpath at app
 * boot, so anything reachable from there lands in the boot chunk of all ten apps. Importing the
 * i18n-bootstrap *barrel* would drag `createAppI18n` — and through it React and `react-i18next` —
 * into every one of them. This module therefore stays out of `index.js`, and its ONE import is the
 * pure plural selector (`./pluralKeys.js` → `../formatterCache.js`: `Intl` only, no React, no
 * i18next — Q-R8CLOSE-2), because the degrade path must pick the plural leaf i18next picks.
 *
 * The precedent is `namespaceRegistry.js` next door: the foundational package owns the contract, the
 * composition package owns the instances, and the direction stays one-way
 * (`app → i18n-namespaces → {packages} → i18n-bootstrap`, I18N-BOOTSTRAP-DEPCYCLE-001).
 *
 * ## 🔴 Why `braces` and `onMissing` are PARAMETERS and not constants
 *
 * Both axes are load-bearing product behaviour, asserted by the packages' own suites:
 *
 *   - `@puffin-core/mfa-ui` bundles carry `{{maskedPhone}}` and BLANK an unsupplied token;
 *   - `@puffin-core/re-auth` bundles carry `{operationDescription}` — already-translated prose
 *     handed in by a caller — and ECHO an unsupplied token back so the gap is visible.
 *
 * `packages/mfa-ui/tests/unit/strings.test.js:46-54` says it outright: *"Do not 'unify' them without
 * changing both bundles."* Collapsing either axis here would silently change rendered copy in a
 * security-path screen. So the kit unifies the IMPLEMENTATION and keeps the two contracts.
 *
 * @module @puffin-core/i18n-bootstrap/stringsKit
 */

import { selectPluralKey } from './pluralKeys.js';

/**
 * Placeholder patterns by brace style, as SOURCE strings.
 *
 * A fresh `RegExp` is built per interpolator rather than sharing one module-level global regex:
 * `String.prototype.replace` does reset `lastIndex`, but a shared global regex is one refactor away
 * from an `.exec`/`.test` caller that does not, and that bug reads as "every other call fails".
 *
 * @constant {Readonly<Record<string, string>>}
 */
const PLACEHOLDER_SOURCE = Object.freeze({
  // 🔴 The lookaround is not decoration. A bare `\{(\w+)\}` also matches the INNER braces of a
  // `{{name}}` token, so a double-brace leaf fed to a single-brace interpolator renders `{value}` —
  // half-substituted, which reads as corruption rather than as a missing translation. That case is
  // not hypothetical during I18N-RENDER-002: prop-injected catalogs carrying `{{count}}`
  // (`userProfilePage`) and `{{label}}` (`backLink`) are being folded into namespaces whose other
  // leaves use single braces. With the lookaround, a mis-converted leaf renders its token INTACT —
  // visibly wrong, and caught by the package's own render proof instead of shipping.
  single: '(?<!\\{)\\{(\\w+)\\}(?!\\})',
  double: '\\{\\{(\\w+)\\}\\}',
});

/**
 * What an interpolator does with a placeholder no caller supplied a value for.
 *
 * @constant {ReadonlyArray<string>}
 */
const MISSING_POLICIES = Object.freeze(['keep', 'empty']);

/**
 * Build the interpolator for one package's placeholder convention.
 *
 * @param {Object} [options]
 * @param {'single'|'double'} [options.braces='single'] `{name}` or `{{name}}`.
 * @param {'keep'|'empty'} [options.onMissing='keep'] Leave an unsupplied placeholder intact
 *   (`keep` — the gap stays visible) or replace it with the empty string (`empty`).
 * @returns {(template: unknown, params?: unknown) => string} The interpolator.
 * @throws {TypeError} On an unknown `braces` or `onMissing`, naming the offending value.
 */
export function createInterpolator({ braces = 'single', onMissing = 'keep' } = {}) {
  const source = PLACEHOLDER_SOURCE[braces];
  if (typeof source !== 'string') {
    throw new TypeError(
      `createInterpolator: unknown braces "${braces}". Supported: ${Object.keys(
        PLACEHOLDER_SOURCE,
      ).join(', ')}.`,
    );
  }
  if (!MISSING_POLICIES.includes(onMissing)) {
    throw new TypeError(
      `createInterpolator: unknown onMissing "${onMissing}". Supported: ${MISSING_POLICIES.join(
        ', ',
      )}.`,
    );
  }

  const pattern = new RegExp(source, 'g');
  const blankMissing = onMissing === 'empty';

  return function interpolate(template, params) {
    // A non-string template returns '' rather than being handed back: three of the copies this
    // replaces crashed here, and one returned the value unchanged — which is how `undefined` reaches
    // a screen. There is no caller for whom rendering the literal text "undefined" is correct.
    if (typeof template !== 'string') return '';
    const values = params !== null && typeof params === 'object' ? params : {};

    return template.replace(pattern, (match, name) => {
      // OWN properties only. Without this, `{toString}` renders a function body.
      if (!Object.prototype.hasOwnProperty.call(values, name)) {
        return blankMissing ? '' : match;
      }
      const value = values[name];
      // An explicitly nullish value is MISSING, not the string "null" — the direction that matters,
      // because `String(null)` in an aria-label reads to a user as a defect.
      if (value === undefined || value === null) return blankMissing ? '' : match;
      return String(value);
    });
  };
}

/**
 * Is a translator's return usable, or does it mean *missing*?
 *
 * 🔴 The `value !== key` arm is the one that matters and the one hand-rolled copies got wrong:
 * react-i18next returns **the key itself** for an unregistered key, so `t(key) || english` renders
 * `mfa.title` as visible product copy in any app that never registered the namespace.
 *
 * @param {unknown} value The translator's return.
 * @param {string} key The key it was asked for.
 * @returns {boolean} Whether the value can be used.
 */
export function isUsableTranslation(value, key) {
  return value !== undefined && value !== null && value !== '' && value !== key;
}

/**
 * Build one package's `resolveString(t, key, params)`: consumer translator → English default.
 *
 * @param {Object} defaults The package's frozen English bundle (`DEFAULT_*_STRINGS`).
 * @returns {(t: unknown, key: string, params?: Object) => string|undefined} The resolver.
 * @throws {TypeError} When `defaults` is not an object.
 */
/**
 * Read `a.b.c` out of a bundle, the way i18next reads a key.
 *
 * 🔴 Dotted-path aware, NOT a flat `bundle[key]`, and that is the whole point: the degrade path must
 * have the same lookup semantics as the real path. A flat lookup silently returns `undefined` for
 * every key in a NESTED bundle — measured during I18N-RENDER-002, where `page-shell` (30 top-level
 * keys, 0 string-valued) and `onboarding-ui` (6, 0) each shipped a `resolveString` that could not
 * resolve a single real key while passing every instrument aimed at it.
 *
 * A bundle may also be MIXED — `@puffin-core/auth-context` holds four flat leaves beside a nested
 * `codes` group — so "flat or nested" was never the right question to ask of a bundle.
 *
 * Returns `undefined` for a missing path, a path that runs through a non-object, or a path landing
 * on a non-string; the caller treats all three the same way (no default available).
 *
 * @param {Object} bundle - the English bundle.
 * @param {string} key - a leaf key or a dotted path.
 * @returns {string|undefined}
 */
function lookupPath(bundle, key) {
  if (typeof key !== 'string' || key.length === 0) return undefined;
  // Fast path AND the exact-key-wins rule: a bundle may legitimately hold a literal dotted key.
  if (Object.prototype.hasOwnProperty.call(bundle, key)) {
    return typeof bundle[key] === 'string' ? bundle[key] : undefined;
  }
  if (!key.includes('.')) return undefined;
  let node = bundle;
  for (const segment of key.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    if (!Object.prototype.hasOwnProperty.call(node, segment)) return undefined;
    node = node[segment];
  }
  return typeof node === 'string' ? node : undefined;
}

/**
 * The locale every package's DEFAULTS bundle is written in. The defaults bundle IS the package's
 * ENGLISH catalog — the degrade path a component renders when its namespace is not registered — so
 * this is fixed by what the data is, not chosen for a viewer. Declared by name so
 * `no-hardcoded-intl-locale` admits it (Q-R8MIRROR2-7).
 *
 * @constant {string}
 */
export const DEFAULTS_BUNDLE_LOCALE = 'en';

/**
 * The key to look up in the English bundle for `params` (Q-R8MIRROR2-7).
 *
 * i18next selects `<key>_<category>` from `Intl.PluralRules` when a `count` is in scope; the degrade
 * path picks the SAME leaf, through the one shared selector (`./pluralKeys.js`, i18next's rule —
 * Q-R8CLOSE-2), so a package can author `_one` / `_other` leaves and render the same whether or not
 * its namespace is registered. A template with no suffixed sibling keeps its bare key; a missing or
 * string `count` is the bare key, as i18next renders it.
 *
 * @param {Object} bundle
 * @param {string} key
 * @param {Object|undefined} params
 * @returns {string|undefined} the English template, or `undefined` when the bundle has none
 */
function lookupWithPlural(bundle, key, params) {
  const count = params ? params.count : undefined;
  const selected = selectPluralKey(
    key,
    count,
    DEFAULTS_BUNDLE_LOCALE,
    (candidate) => lookupPath(bundle, candidate) !== undefined,
  );
  return lookupPath(bundle, selected);
}

export function createStringResolver(defaults) {
  if (defaults === null || typeof defaults !== 'object') {
    throw new TypeError(
      "createStringResolver: `defaults` must be the package's English bundle object. Without it " +
        'every miss would throw instead of degrading to English, and the failure would surface as ' +
        'a blank screen far from its cause.',
    );
  }

  return function resolveString(t, key, params) {
    if (typeof t === 'function') {
      // ⚠️ `params` is forwarded ONLY when supplied: `t(key, undefined)` is not the same call as
      // `t(key)`, and sibling suites assert the exact arity with `toHaveBeenCalledWith(key)`.
      const value = params !== undefined ? t(key, params) : t(key);
      if (isUsableTranslation(value, key)) return value;
    }
    return lookupWithPlural(defaults, key, params);
  };
}
